import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { toBooleanOrNull } from "@utils/generic-functions";

/**
 * Hook personalizado para manejar query parameters de forma eficiente
 *
 * @returns {Object} Objeto con métodos para manejar query params
 */
export const useQueryParams = () => {
  const location = useLocation();

  // Memoizar URLSearchParams para evitar recálculos innecesarios
  const searchParams = useMemo(() => new URLSearchParams(location.search), [
    location.search,
  ]);

  // Métodos para obtener diferentes tipos de valores
  const methods = useMemo(
    () => ({
      // Obtener valor raw como string
      get: (key) => searchParams.get(key),

      // Obtener como boolean o null (útil para flags)
      getBoolean: (key) => toBooleanOrNull(searchParams.get(key)),

      // Obtener como número
      getNumber: (key) => {
        const value = searchParams.get(key);
        return value ? Number(value) : null;
      },

      // Obtener múltiples valores como objeto
      getMultiple: (keys) => {
        return keys.reduce((acc, key) => {
          acc[key] = searchParams.get(key);
          return acc;
        }, {});
      },

      // Obtener múltiples valores como booleans
      getMultipleBooleans: (keys) => {
        return keys.reduce((acc, key) => {
          acc[key] = toBooleanOrNull(searchParams.get(key));
          return acc;
        }, {});
      },

      // Verificar si existe un parámetro
      has: (key) => searchParams.has(key),

      // Obtener todos los params como objeto
      getAll: () => {
        const params = {};
        for (const [key, value] of searchParams.entries()) {
          params[key] = value;
        }
        return params;
      },

      // Acceso directo al objeto URLSearchParams
      searchParams,
    }),
    [searchParams]
  );

  return methods;
};

/**
 * Hook específico para módulos hospitalarios que usan parámetros comunes
 *
 * @returns {Object} Parámetros específicos de hospitalización
 */
export const useHospitalizationQueryParams = () => {
  const queryParams = useQueryParams();

  return useMemo(
    () => ({
      isScheduledSurgery: queryParams.getBoolean("cirugia_programada"),
      isNewCase: queryParams.getBoolean("nueva"),
      patientId: queryParams.get("patient_id"),
      caseId: queryParams.get("case_id"),
      // Acceso a métodos base
      ...queryParams,
    }),
    [queryParams]
  );
};

export default useQueryParams;
